import type { BoxProps } from '@hover/blueprint';
import { Box, Button, Panel, Spinner } from '@hover/blueprint';
import { iShare, iX } from '@hover/icons';
import { captureException } from '@sentry/react';
import { useMachine } from '@xstate/react';
import React, { useCallback, useContext, useEffect, useRef } from 'react';

import {
  LANDSCAPE_TOOLBAR_MENU_WIDTH,
  LANDSCAPE_TOOLBAR_WIDTH,
} from 'src/modules/designStudio/components/Toolbar/toolBarConstants';
import { Menu } from 'src/modules/designStudio/constants';
import type { DesignBuilding } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import type { ExtendedMacheteViewer } from 'src/modules/designStudio/helpers/machete';
import type {
  Geometry,
  Metadata,
} from 'src/modules/designStudio/hooks/useFetchModelData/types';
import { createMachine } from 'src/modules/designStudio/machines/building';
import type { Textures } from 'src/modules/designStudio/machines/productCatalog';
import {
  MacheteBuildingContext,
  Mode,
  historyContext,
} from 'src/modules/designStudio/providers';
import { context } from 'src/modules/designStudio/providers/DesignStudioServiceProvider';
import type { TrackingMetadata } from 'src/modules/designStudio/types';
import { CustomIcon, Image } from 'src/shared/components';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import {
  TranslationNamespace,
  useIsLandscape,
  useMode,
  useReactNativeWebView,
  useSplit,
  useTranslate,
} from 'src/shared/hooks';
import { MobileAppActionEnum } from 'src/shared/hooks/useReactNativeWebView/types';
import { Page, buttonPressed } from 'src/shared/telemetry';
import { logInfo } from 'src/shared/telemetry/logger';

interface BuildingProps {
  jobId?: number;
  canvasHeight: string;
  setLoadingBuilding: (loadingBuilding: boolean) => void;
  setViewer?: (viewer: ExtendedMacheteViewer) => void;
  menu: Menu | null;
  textures: Textures;
  geometry: Geometry;
  metadata: Metadata;
  trackingMetadata?: TrackingMetadata;
}

const Building = React.memo(
  ({
    jobId,
    setLoadingBuilding,
    canvasHeight,
    setViewer,
    menu,
    textures,
    geometry,
    metadata,
    trackingMetadata,
  }: BuildingProps) => {
    const mode = useMode();

    const { client: clientPromise } = useSplit();
    const [isLandscape] = useIsLandscape();
    const canvasRef = useRef<HTMLDivElement | null>(null);

    const { setBuilding } = useContext(MacheteBuildingContext);

    const { historyState, clearInspireMeImage } = useContext(historyContext);

    const { history } = historyState;

    const [buildingMachineState, send] = useMachine(() => {
      const buildingMachine = createMachine();
      return buildingMachine.withContext({
        ...buildingMachine.context,
        jobId,
        canvasRef,
        textures,
        geometry,
        metadata,
        splitClient: clientPromise,
        mode,
        trackingMetadata,
      });
    });

    const building: DesignBuilding = buildingMachineState.context
      .building as DesignBuilding;
    const { viewer, sentHistoryLoadedEvent } = buildingMachineState.context;

    useEffect(() => {
      setBuilding(building);
    }, [setBuilding, building]);

    const historyDesignConfig = history?.[0]?.design_config || null;

    if (!sentHistoryLoadedEvent && historyDesignConfig) {
      send({
        type: 'HISTORY_LOADED',
        savedStylesToUpdate: historyDesignConfig,
      });
    }

    useEffect(() => {
      if (viewer && setViewer) {
        setViewer(viewer);
      }
    }, [setViewer, viewer]);

    useEffect(() => {
      send({
        type: 'CHANGE_VIEW',
        menu,
        canvasHeight,
      });
    }, [send, canvasHeight, menu, canvasRef]);

    useEffect(() => {
      if (building) {
        setLoadingBuilding(false);
      }
    }, [setLoadingBuilding, building]);

    const containerStyles: BoxProps = {
      overflow: 'none',
      position: 'absolute',
      top: 0,
      right: 0,
    };
    if (isLandscape) {
      const expandedMenuWidth = `calc(100% - ${
        LANDSCAPE_TOOLBAR_WIDTH + LANDSCAPE_TOOLBAR_MENU_WIDTH
      }px)`;
      const noMenuWidth = '100%';
      containerStyles.width =
        menu && menu !== Menu.SHARE ? expandedMenuWidth : noMenuWidth;
      containerStyles.height = '100vh';
    } else {
      containerStyles.width = '100vw';
      containerStyles.height = canvasHeight;
    }
    containerStyles.transition = menu ? 'width 600ms ease-out' : '';

    const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
    const shareInspireMeImageText = t('menu.inspireMe.share');
    const [isWebview, sendMessageToWebView] = useReactNativeWebView();
    const shareInspireMeImage = useCallback(() => {
      if (historyState.inspireMeImageURL) {
        try {
          let imageName = historyState.inspireMeImageURL.split('/').pop();
          if (imageName) {
            imageName = imageName.replace(/\.[^/.]+$/, '');
            const url = `https://hover.to/designer/share/${imageName}`;
            const canNativelyShare = navigator.canShare && navigator.canShare();
            if (canNativelyShare) {
              void navigator.share({
                text: shareInspireMeImageText,
                url,
              });
            } else if (isWebview) {
              logInfo('ShareInspireMeImage: navigator.canShare not supported');
              sendMessageToWebView(
                JSON.stringify({
                  action: MobileAppActionEnum.OPEN_LINK,
                  data: url,
                }),
              );
            } else {
              window.open(url, '_blank');
            }
          }
        } catch (error) {
          captureException(error);
        }
      }
    }, [
      historyState.inspireMeImageURL,
      shareInspireMeImageText,
      isWebview,
      sendMessageToWebView,
    ]);

    const { appService } = useContext(context);
    const appSnapshot = appService.getSnapshot
      ? appService.getSnapshot()
      : {
          context: {
            orgId: undefined,
            partnerId: undefined,
            userId: undefined,
          },
        };

    const inspireMeFrozenRenderFilter =
      historyState.inspireMeImageURL !== '' ? 'blur(1px)' : 'brightness(60%)';

    /**
     * We pass in templateDesignConfigFlag as a parameter so that when it loads we will re-render each config componenet,
     * which will in turn invoke updateStyles to apply the style to the building.
     *
     * We show the Inspire Me image on top of the Machete render whenever it's available.
     */
    return (
      <Box>
        <Box testId="canvas-container" {...containerStyles}>
          <Box height="100%" ref={canvasRef} width="100%" />
          {historyState.inspireMeImageURL !== '' && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                padding: '50px',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url("${historyState.inspireMeImageURL}")`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  borderRadius: '15px',
                  boxShadow: '5px 10px 18px black',
                }}
              >
                <Box display="block" marginBottom={600} width="100%">
                  <Box justifyContent="space-between">
                    <Box alignItems="center" height="100%">
                      <Button
                        fill="minimal"
                        margin={5}
                        onClick={() => {
                          buttonPressed({
                            button_text: 'Share',
                            primary_cta: false,
                            job_id: historyState.jobId ?? undefined,
                            org_id: appSnapshot.context.orgId,
                            partner_id: appSnapshot.context.partnerId,
                            user_id: appSnapshot.context.userId,
                            user_email: historyState.email ?? undefined,
                            page_or_screen_name: Page.INSPIRATION,
                            design_studio_mode: Mode.DEFAULT,
                            feature: FeatureTracking.INSPIRE_ME,
                            button_location: 'inspiration-modal',
                            track_id: 'inspiration-share',
                          });
                          shareInspireMeImage();
                        }}
                        padding={0}
                        position="relative"
                        shape="circle"
                        size="default"
                      >
                        <Panel
                          alignItems="center"
                          borderRadius="50%"
                          height="100%"
                          justifyContent="center"
                          style={{
                            pointerEvents: 'auto',
                          }}
                          width="100%"
                        >
                          <CustomIcon
                            color="neutral700"
                            icon={iShare}
                            iconHeight="20px"
                            iconWidth="20px"
                          />
                        </Panel>
                      </Button>
                    </Box>
                    <Box alignItems="center" height="100%">
                      <Button
                        fill="minimal"
                        margin={5}
                        onClick={() => clearInspireMeImage()}
                        padding={0}
                        shape="circle"
                        size="default"
                      >
                        <Panel
                          alignItems="center"
                          borderRadius="50%"
                          height="100%"
                          justifyContent="center"
                          style={{
                            pointerEvents: 'auto',
                          }}
                          width="100%"
                        >
                          <CustomIcon
                            color="neutral700"
                            icon={iX}
                            iconHeight="20px"
                            iconWidth="20px"
                          />
                        </Panel>
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </div>
            </div>
          )}
          {historyState.inspireMeFrozenRenderURL !== '' && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                filter: inspireMeFrozenRenderFilter,
              }}
            >
              <Box
                alignItems="center"
                backgroundColor="transparent"
                height="100%"
                justifyContent="center"
                position="absolute"
                width="100%"
              >
                <Spinner size="huge" />
              </Box>
              <Image
                alt=""
                src={historyState.inspireMeFrozenRenderURL}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          )}
        </Box>
      </Box>
    );
  },
);

export { Building, BuildingProps };
